import * as React from 'react'
import $ from 'jquery'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { AppMainView, AppView, AppViewWrapper } from '../../../../components/AppView'
import { Sidebar, SidebarItem, SidebarContent } from '../../../../components/AppView/Sidebar'
import PrintView from './PrintView'
import { PrintingStore, presets, PrintProduct } from './PrintingStore'
import Endpoints from '../../../../constants/Endpoints'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { CurrencyInput } from '../../../../components/CurrencyInput'
import { message } from 'antd'
import { SellableProductAsyncInput } from '../../../../components/ProductAsyncInput/SellableProductAsyncInput'
import { Track } from '../../../../Track'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { ILocation } from '@getgreenline/homi-shared'
import { Select } from 'antd-v4'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore

  location: {
    query: {
      useLocalStorage?: 'true' | 'false'
      autoprint?: 'true' | 'false'
      config?: string
      locationName?: string
    }
  }
}

interface State {
  selectedLocation?: number | undefined
}

@inject('currentCompanyStore')
@observer
export class PrintingConfigViewV2 extends React.Component<Props, State> {
  printingStore = new PrintingStore()
  productStore = this.props.currentCompanyStore?.productStore
  locations = this.props.currentCompanyStore?.locations || []

  constructor(props: Props) {
    super(props)

    this.state = {
      selectedLocation: undefined,
    }
  }

  componentDidMount() {
    this.syncWithLocation()
  }

  componentDidUpdate() {
    if (this.props.location.query.autoprint === 'true') {
      // Wait for rendering + CSS to finish before sending the HTML over to the label server
      setTimeout(() => {
        this.downloadLabels(true)
      }, 500)
    }
  }

  syncWithLocation() {
    let config: string | null
    if (this.props.location.query.useLocalStorage === 'true') {
      config = window.localStorage.getItem('printing-config')
    } else {
      config = this.props.location.query.config!
    }
    if (config) {
      const object = JSON.parse(config)
      this.printingStore.populate(this.props.currentCompanyStore!.company!.id, object)
    }
    if (this.props.location.query.locationName) {
      this.printingStore.location = this.props.location.query.locationName
    }
  }
  setLocationId(value: number | undefined) {
    this.printingStore.setLocationId(value)
  }

  downloadLabels(autoprint = false) {
    const config = toJS(this.printingStore)
    const html = '<html><body>' + $('#print-view').html() + '</body></html>'

    // Download labels in a new tab as a post request
    // Reference: https://stackoverflow.com/questions/178964/javascript-post-on-form-submit-open-a-new-window
    const form = document.createElement('form')
    form.setAttribute('method', 'post')
    form.setAttribute('action', Endpoints.PRINTING_API_URL + Endpoints.GET_PDF_LABEL_V2)

    // HTML
    const htmlField = document.createElement('input')
    htmlField.setAttribute('name', 'html')
    htmlField.setAttribute('value', html)
    form.appendChild(htmlField)

    // Page width
    const pageWidthField = document.createElement('input')
    pageWidthField.setAttribute('type', 'number')
    pageWidthField.setAttribute('name', 'pageWidth')
    pageWidthField.setAttribute('value', this.printingStore.pageWidth.toString())
    form.appendChild(pageWidthField)

    // HTML
    const pageHeightField = document.createElement('input')
    pageHeightField.setAttribute('type', 'number')
    pageHeightField.setAttribute('name', 'pageHeight')
    pageHeightField.setAttribute('value', this.printingStore.pageHeight.toString())
    form.appendChild(pageHeightField)

    document.body.appendChild(form)
    // If the url query paramter contains autoprint = true, just print the label right away
    if (!autoprint) {
      // setting form target to a window named 'formresult'
      form.setAttribute('target', 'formresult')
      // creating the 'formresult' window with custom features prior to submitting the form
      window.open('', 'formresult')
    }
    form.submit()
    form!.parentNode!.removeChild(form)

    Track.track('dashboard:products_barcodeLabelsDownloaded')
  }

  get userPermittedLocations() {
    const { currentCompanyStore, currentUserStore } = this.props
    const locations = currentCompanyStore?.locations
    const currentUser = currentUserStore?.currentUser
    const userLocationIds = currentUser?.entityAssignments.map((entities) => entities.entity.id)

    const allAvailableLocationForThisUser = locations?.filter(
      (entity) =>
        userLocationIds?.includes(entity.id) ||
        entity.companyId === currentCompanyStore?.company?.id,
    )

    return allAvailableLocationForThisUser
  }

  render() {
    if (this.printingStore.isLoading) {
      return (
        <div>
          <LoadingAnimation>
            Loading products ({this.printingStore.products.filter((p) => p.title).length}/
            {this.printingStore.products.length})...
          </LoadingAnimation>
        </div>
      )
    }
    const { selectedLocation } = this.state

    const locationId = this.printingStore.locationId
    const locationObj = this.locations.find((l) => l.id === locationId)

    return (
      <div className='full-height'>
        <AppView>
          <Sidebar>
            <SidebarContent>
              {Object.keys(presets).map((key, i) => (
                <SidebarItem
                  key={i}
                  title={key}
                  active={this.printingStore.preset === key}
                  onClick={() => this.printingStore.changePreset(key)}
                />
              ))}
            </SidebarContent>
          </Sidebar>
          <AppViewWrapper>
            <AppMainView>
              <div className='row'>
                <div className='col-md-8'>
                  <div className='row'>
                    <div className='col'>
                      <div>
                        <h5>Print settings</h5>
                      </div>
                    </div>

                    <div className='col'>
                      <div className='d-flex flex-row-reverse '>
                        <BlazeButton
                          buttonProps={{
                            className: 'w-20',
                            type: 'primary',
                            size: 'large',
                            onClick: (e) => {
                              e.preventDefault()
                              this.downloadLabels()
                            },
                          }}
                          titleProps={{
                            title: '+ Generate PDF',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label>Store label</label>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      value={this.printingStore.location}
                      onChange={(e) => (this.printingStore.location = e.target.value)}
                    />
                  </div>

                  <div className='row'>
                    <div className='col-md-3'>
                      <div className='form-group'>
                        <input
                          type='checkbox'
                          className='mr-2'
                          checked={this.printingStore.displayBarcode}
                          onChange={(e) => this.printingStore.setDisplayBarcode(e.target.checked)}
                        />
                        <span>Display barcode</span>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='form-group'>
                        <input
                          type='checkbox'
                          className='mr-2'
                          checked={this.printingStore.displayBarcodeNumber}
                          onChange={(e) =>
                            this.printingStore.setDisplayBarcodeNumber(e.target.checked)
                          }
                        />
                        <span>Display barcode text</span>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='form-group'>
                        <input
                          type='checkbox'
                          className='mr-2'
                          checked={this.printingStore.displayPrice}
                          onChange={(e) => this.printingStore.setDisplayPrice(e.target.checked)}
                        />
                        <span>Display price</span>
                      </div>
                    </div>

                    <div className='col-md-3'>
                      <div className='form-group'>
                        <input
                          type='checkbox'
                          className='mr-2'
                          checked={this.printingStore.useLegacyPrintingStyles}
                          onChange={(e) =>
                            this.printingStore.setLegacyPrintingStyles(e.target.checked)
                          }
                        />
                        <span>Legacy styles</span>
                      </div>
                    </div>
                  </div>
                  <div className='row align-items-end'>
                    <div className='col-md-8'>
                      <Select<number>
                        className='gr-filter-item gr-filter-gutter'
                        style={{ width: 165 }}
                        placeholder='Locations'
                        value={selectedLocation || locationId}
                        onSelect={(locationId) => {
                          this.setState({ selectedLocation: locationId })
                        }}
                      >
                        {this.userPermittedLocations?.map((loc) => (
                          <Select.Option key={loc.id} value={loc.id}>
                            {loc.name}
                          </Select.Option>
                        ))}
                      </Select>

                      <BlazeButton
                        buttonProps={{
                          type: 'primary',
                          onClick: () => {
                            if (selectedLocation) {
                              this.setLocationId(selectedLocation)
                            }
                          },
                          className: 'mr-2',
                        }}
                        titleProps={{ title: 'Apply' }}
                      />
                      <BlazeButton
                        buttonProps={{
                          className: 'gr-filter-gutter',
                          onClick: () => {
                            this.setState({ selectedLocation: undefined })
                            this.setLocationId(undefined)
                          },
                        }}
                        titleProps={{ title: 'Reset' }}
                      />
                    </div>

                    <div className='col-md align-items-end d-flex flex-row-reverse'>
                      <a
                        onClick={() =>
                          (this.printingStore.showAdvanced = !this.printingStore.showAdvanced)
                        }
                      >
                        Advanced
                      </a>
                    </div>
                  </div>

                  {this.printingStore.showAdvanced && (
                    <div className='row mt-2'>
                      <div className='col-md-12'>
                        <div className='row'>
                          <div className='col-md-3'>
                            <div className='form-group'>
                              <label>Label width</label>
                              <div className='input-group input-group-sm'>
                                <input
                                  type='number'
                                  className='form-control'
                                  value={this.printingStore.labelWidth}
                                  step='0.1'
                                  min='0'
                                  onChange={(e) =>
                                    (this.printingStore.labelWidth = parseFloat(e.target.value))
                                  }
                                />
                                <div className='input-group-append'>
                                  <div className='input-group-text'>inches</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3'>
                            <div className='form-group'>
                              <label>Label height</label>
                              <div className='input-group input-group-sm'>
                                <input
                                  type='number'
                                  className='form-control'
                                  value={this.printingStore.labelHeight}
                                  step='0.1'
                                  min='0'
                                  onChange={(e) =>
                                    (this.printingStore.labelHeight = parseFloat(e.target.value))
                                  }
                                />
                                <div className='input-group-append'>
                                  <div className='input-group-text'>inches</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3'>
                            <div className='form-group'>
                              <label>Page width</label>
                              <div className='input-group input-group-sm'>
                                <input
                                  type='number'
                                  className='form-control'
                                  value={this.printingStore.pageWidth}
                                  step='0.1'
                                  min='0'
                                  onChange={(e) =>
                                    (this.printingStore.pageWidth = parseFloat(e.target.value))
                                  }
                                />
                                <div className='input-group-append'>
                                  <div className='input-group-text'>inches</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3'>
                            <div className='form-group'>
                              <label>Page height</label>
                              <div className='input-group input-group-sm'>
                                <input
                                  type='number'
                                  className='form-control'
                                  value={this.printingStore.pageHeight}
                                  step='0.1'
                                  min='0'
                                  onChange={(e) =>
                                    (this.printingStore.pageHeight = parseFloat(e.target.value))
                                  }
                                />
                                <div className='input-group-append'>
                                  <div className='input-group-text'>inches</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3'>
                            <div className='form-group'>
                              <label>Margin top</label>
                              <div className='input-group input-group-sm'>
                                <input
                                  type='number'
                                  className='form-control'
                                  value={this.printingStore.marginTop}
                                  step='0.1'
                                  min='0'
                                  onChange={(e) =>
                                    (this.printingStore.marginTop = parseFloat(e.target.value))
                                  }
                                />
                                <div className='input-group-append'>
                                  <div className='input-group-text'>inches</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3'>
                            <div className='form-group'>
                              <label>Margin bottom</label>
                              <div className='input-group input-group-sm'>
                                <input
                                  type='number'
                                  className='form-control'
                                  value={this.printingStore.marginBottom}
                                  step='0.1'
                                  min='0'
                                  onChange={(e) =>
                                    (this.printingStore.marginTop = parseFloat(e.target.value))
                                  }
                                />
                                <div className='input-group-append'>
                                  <div className='input-group-text'>inches</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <br />
                  {this.printingStore.paginatedProductsWithBarcode.map((product, index) => {
                    return (
                      <div className='background-shadow mb-2 p-4' key={index}>
                        {!product.productId && (
                          <div className='form-group'>
                            <SellableProductAsyncInput
                              optionFilterFunction={(option) => option.filter((p) => p.barcode)}
                              placeholder='Search products'
                              onSelect={(asyncProduct) => {
                                const shortName = asyncProduct.shortName
                                const fullName = asyncProduct.parentProductName
                                  ? `${asyncProduct.parentProductName} - ${asyncProduct.name}`
                                  : asyncProduct.name

                                product.productId = asyncProduct.id
                                product.title = shortName || fullName
                                product.barcode = asyncProduct.barcode
                                product.price = asyncProduct.price
                                product.sku = asyncProduct.sku
                                product.overridePrices = asyncProduct.overridePrices
                              }}
                            />
                          </div>
                        )}
                        <div className='form-group'>
                          <div className='input-group input-group-sm'>
                            <div className='input-group-prepend'>
                              <span className='input-group-text'>Product name</span>
                            </div>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={product.title || ''}
                              onChange={(e) => (product.title = e.target.value)}
                            />
                          </div>
                        </div>
                        <div className='row'>
                          <div className='col-5'>
                            <div className='form-group'>
                              <div className='input-group input-group-sm'>
                                <div className='input-group-prepend'>
                                  <span className='input-group-text'>Barcode</span>
                                </div>
                                <input
                                  type='text'
                                  className='form-control form-control-sm'
                                  value={product.barcode || ''}
                                  onChange={(e) => (product.barcode = e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className='col-7 '>
                            <div className='form-group'>
                              <div className='input-group input-group-sm'>
                                <div className='input-group-prepend'>
                                  <span className='input-group-text'>SKU</span>
                                </div>
                                <input
                                  type='text'
                                  className='form-control form-control-sm'
                                  value={product.sku || ''}
                                  onChange={(e) => (product.sku = e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='row '>
                          <div className='col-5'>
                            <div className='form-group'>
                              <div className='input-group input-group-sm'>
                                <Select<string | number>
                                  showArrow={false}
                                  style={{ minWidth: 160 }}
                                  maxTagTextLength={1}
                                  open={false}
                                  placeholder='Locations'
                                  dropdownMatchSelectWidth={false}
                                  value={product.overridePrices && locationObj?.name}
                                  disabled={true}
                                ></Select>
                              </div>
                            </div>
                          </div>
                          <div className='col-7'>
                            <div className='form-group'>
                              <div className='input-group input-group-sm'>
                                <div className='input-group-prepend'>
                                  <span className='input-group-text'>Price</span>
                                </div>
                                {(() => {
                                  const mappedLocationIdsAndPrice = new Map<number, number | null>()
                                  let price: number | null = product.price
                                  if (locationId) {
                                    product.overridePrices?.map((overridePrice) => {
                                      locationId &&
                                        mappedLocationIdsAndPrice.set(
                                          overridePrice.locationId,
                                          overridePrice.retailPrice,
                                        )
                                    })
                                    const locationPrice = mappedLocationIdsAndPrice?.get(locationId)

                                    price = locationPrice || product.price
                                  }

                                  return (
                                    <CurrencyInput
                                      key=''
                                      className='form-control form-control-sm'
                                      centValue={price}
                                      onChange={(e) => (product.price = e)}
                                    />
                                  )
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className='row'>
                          <div className='col-5'>
                            <div className='form-group'>
                              <div className='input-group input-group-sm'>
                                <div className='input-group-prepend'>
                                  <span className='input-group-text'>Total quantity</span>
                                </div>
                                <input
                                  type='number'
                                  className='form-control form-control-sm'
                                  value={product.quantity}
                                  min={1}
                                  onChange={(e) => (product.quantity = parseFloat(e.target.value))}
                                />
                              </div>
                            </div>
                          </div>
                          <div className='col-7'>
                            <div className='form-group'>
                              <div className='input-group input-group-sm'>
                                <div className='input-group-prepend'>
                                  <span className='input-group-text'>Quantity on page</span>
                                </div>
                                <input
                                  className='form-control form-control-sm'
                                  value={this.printingStore.getQuantityOfProductOnPage(
                                    product.barcode,
                                  )}
                                  disabled
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <a onClick={() => this.printingStore.removeProduct(product.barcode)}>
                            Remove
                          </a>
                        </div>
                      </div>
                    )
                  })}
                  <br />
                  <div>
                    <a
                      onClick={() =>
                        this.printingStore.addProduct({
                          productId: null,
                          barcode: '',
                          quantity: 1,
                          price: null,
                          goToLastPage: true,
                          sku: null,
                        })
                      }
                    >
                      Add product
                    </a>
                  </div>
                  <br />

                  <div className='d-flex' style={{ flexWrap: 'wrap' }}>
                    {new Array(this.printingStore.numberOfPages).fill(0).map((nothing, index) => {
                      const pageNumber = index + 1
                      if (this.printingStore.currentPage === pageNumber) {
                        return (
                          <BlazeButton
                            key={index}
                            buttonProps={{
                              className: 'mr-2 mb-2',
                            }}
                            titleProps={{
                              title: `Page ${pageNumber}`,
                            }}
                          />
                        )
                      } else {
                        return (
                          <BlazeButton
                            key={index}
                            buttonProps={{
                              className: 'mr-2 mb-2',
                              onClick: () => this.printingStore.setCurrentPage(pageNumber),
                            }}
                            titleProps={{
                              title: `Page ${pageNumber}`,
                            }}
                          />
                        )
                      }
                    })}
                  </div>
                  <div className='text-muted mb-2'>
                    You can print up to {this.printingStore.labelsPerPage} labels at a time
                  </div>

                  <br />
                  <br />
                </div>
                <div className='col-md-4 bg-light'>
                  <br />
                  <PrintView printingStore={this.printingStore} />
                  <br />
                </div>
              </div>
            </AppMainView>
          </AppViewWrapper>
        </AppView>
      </div>
    )
  }
}
